import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  Tabs,
  Tab,
  Button,
  Alert,
  Grid,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import apiClient, { getErrorMessage } from '../../utils/axios';
import { formatKstDate, formatNumber, daysUntil } from '../../utils/formatUtils';
import { PageHeader, StatCard, SubscriptionChip, useConfirm, useNotify } from '../../components/common';
import { GrantSubscriptionDialog, ExtendSubscriptionDialog, pendingActions } from '../../components/SubscriptionDialogs';

const STATUS_TABS = [
  { value: 'pending', label: '입금대기' },
  { value: 'active', label: '구독중' },
  { value: 'expiring', label: '만료 임박 (7일)' },
  { value: 'expired', label: '만료' },
  { value: 'cancelled', label: '취소' },
  { value: '', label: '전체' },
];

const Subscriptions = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [confirmDialog, ask] = useConfirm();
  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [extendTarget, setExtendTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        apiClient.get('/admin/subscriptions', { params: { status, search: debounced, page, pageSize, expiringDays: 7 } }),
        apiClient.get('/admin/subscriptions/stats'),
      ]);
      setData(list.data);
      setStats(st.data);
      setError('');
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [status, debounced, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const pending = pendingActions({ ask, notify, onDone: load });

  const cancel = (sub) =>
    ask({
      title: '구독 취소',
      message: `${sub.username || sub.userId} 회원의 ${sub.planName} 구독을 즉시 취소합니다.\n회원의 에이전트는 다음 동기화 때 자동매매를 끕니다.`,
      confirmText: '구독 취소',
      danger: true,
      onConfirm: async () => {
        try {
          const res = await apiClient.post(`/admin/subscriptions/${sub.id}/cancel`);
          notify(res.data.message, res.data.ok ? 'success' : 'warning');
          load();
        } catch (e) {
          notify(getErrorMessage(e), 'error');
        }
      },
    });

  return (
    <>
      <PageHeader
        title="서버구독 관리"
        description="유료 구독 부여·연장·취소. 변경 내용은 회원 에이전트가 다음 동기화(최대 10분) 때 반영합니다."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGrantOpen(true)}>
            구독 부여
          </Button>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="유료 구독 회원" value={formatNumber(stats?.active_users)} color="primary.main" onClick={() => setStatus('active')} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="7일 내 만료" value={formatNumber(stats?.expiring_7d)} color="warning.main" onClick={() => setStatus('expiring')} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="최근 30일 신규·갱신" value={formatNumber(stats?.started_30d)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="최근 30일 결제 금액" value={`${formatNumber(stats?.revenue_30d)}원`} />
        </Grid>
      </Grid>

      <Paper>
        <Tabs
          value={status}
          onChange={(_e, v) => {
            setStatus(v);
            setPage(1);
          }}
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {STATUS_TABS.map((t) => (
            <Tab
              key={t.value || 'all'}
              value={t.value}
              label={t.value === 'pending' && stats?.pending_count ? `${t.label} (${stats.pending_count})` : t.label}
            />
          ))}
        </Tabs>
        <TextField
          size="small"
          placeholder="이메일 · 이름 · 회원ID · 결제번호"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ m: 2, width: 360 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>회원</TableCell>
              <TableCell>요금제</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>시작</TableCell>
              <TableCell>만료</TableCell>
              <TableCell align="right">남은 일수</TableCell>
              <TableCell align="right">금액</TableCell>
              <TableCell>메모</TableCell>
              <TableCell align="right">관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  구독이 없습니다.
                </TableCell>
              </TableRow>
            )}
            {data.items.map((s) => {
              const left = s.status === 'active' ? daysUntil(s.expiresAt) : null;
              return (
                <TableRow key={s.id} hover>
                  <TableCell>{s.id}</TableCell>
                  <TableCell sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/users/${encodeURIComponent(s.userId)}`)}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.username || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.email} · #{s.userId}</Typography>
                  </TableCell>
                  <TableCell>{s.planName} ({s.billingCycleLabel})</TableCell>
                  <TableCell><SubscriptionChip status={s.status} /></TableCell>
                  <TableCell>{formatKstDate(s.startedAt)}</TableCell>
                  <TableCell>{formatKstDate(s.expiresAt)}</TableCell>
                  <TableCell align="right" sx={{ color: left != null && left <= 7 ? 'warning.main' : undefined }}>
                    {left != null ? `${left}일` : '-'}
                  </TableCell>
                  <TableCell align="right">{s.amount != null ? `${formatNumber(s.amount)}원` : '-'}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{[s.paymentId, s.memo].filter(Boolean).join(' / ') || '-'}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {s.status === 'pending' && (
                      <>
                        <Button size="small" variant="contained" onClick={() => pending.approve(s)}>승인</Button>
                        <Button size="small" color="error" onClick={() => pending.reject(s)}>거절</Button>
                      </>
                    )}
                    {(s.status === 'active' || s.status === 'expired') && (
                      <Button size="small" onClick={() => setExtendTarget(s)}>기간 변경</Button>
                    )}
                    {s.status === 'active' && (
                      <Button size="small" color="error" onClick={() => cancel(s)}>취소</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data.total}
          page={page - 1}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[20, 50, 100, 200]}
          onPageChange={(_e, p) => setPage(p + 1)}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          labelRowsPerPage="페이지당"
        />
      </Paper>

      <GrantSubscriptionDialog open={grantOpen} onClose={() => setGrantOpen(false)} onDone={load} />
      <ExtendSubscriptionDialog open={!!extendTarget} subscription={extendTarget} onClose={() => setExtendTarget(null)} onDone={load} />
      {confirmDialog}
    </>
  );
};

export default Subscriptions;
